import React, {useCallback, useEffect, useState} from "react";
import {Department} from "../../../types/userManagement";
import {platformAPI} from "../../../api/axiosConfig";
import {SelectList, SelectListItem, SelectListPage} from "../../../components/select-list/SelectList";
import {useEvents} from "../../../lib/useEvents";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PublicIcon from "@mui/icons-material/Public";
import DomainIcon from "@mui/icons-material/Domain";

export type ScopeClient = {
    id: string;
    name: string;
};

export type ScopeSelection = {
    client: ScopeClient | null; // null = system default (no clientID)
    department: Department | null; // null = client level, no department
};

type SirenExistence = {client: boolean; departments: number};

type ClientItem = {
    id: string;
    name: string;
    sirenBroadcastConfig?: SirenExistence;
    sirenDeliveryMethods?: SirenExistence;
    sirenEmailBranding?: SirenExistence;
    sirenPhoneCallConfig?: SirenExistence;
    sirenSMSConfig?: SirenExistence;
};

type DeptItem = {
    id: string;
    name: string;
    hasSirenBroadcastConfig?: boolean;
    hasSirenDeliveryMethods?: boolean;
    hasSirenEmailBranding?: boolean;
    hasSirenPhoneCallConfig?: boolean;
    hasSirenSMSConfig?: boolean;
};

const PAGE_SIZE = 12;

function Dot({active}: {active: boolean}) {
    return (
        <FiberManualRecordIcon
            style={{fontSize: 10}}
            className={active ? "text-green" : "text-light-light-grey"}
        />
    );
}

function ClientItemRow({item, hydrate}: {item: ClientItem; selected: boolean; hydrate?: string}) {
    const showBroadcast = hydrate?.includes("broadcastConfigExistence");
    const showDelivery = hydrate?.includes("deliveryMethodsExistence");
    const showEmail = hydrate?.includes("emailBrandingExistence");
    const showPhone = hydrate?.includes("phoneCallConfigExistence");
    const showSMS = hydrate?.includes("smsConfigExistence");
    const {listen} = useEvents();

    const [broadcastClient, setBroadcastClient] = useState(!!item.sirenBroadcastConfig?.client);
    const [broadcastDepts, setBroadcastDepts] = useState(item.sirenBroadcastConfig?.departments ?? 0);
    const [deliveryClient, setDeliveryClient] = useState(!!item.sirenDeliveryMethods?.client);
    const [deliveryDepts, setDeliveryDepts] = useState(item.sirenDeliveryMethods?.departments ?? 0);
    const [emailClient, setEmailClient] = useState(!!item.sirenEmailBranding?.client);
    const [emailDepts, setEmailDepts] = useState(item.sirenEmailBranding?.departments ?? 0);
    const [phoneClient, setPhoneClient] = useState(!!item.sirenPhoneCallConfig?.client);
    const [phoneDepts, setPhoneDepts] = useState(item.sirenPhoneCallConfig?.departments ?? 0);
    const [smsClient, setSMSClient] = useState(!!item.sirenSMSConfig?.client);
    const [smsDepts, setSMSDepts] = useState(item.sirenSMSConfig?.departments ?? 0);

    useEffect(() => {
        const cleanups: (() => void)[] = [];
        if (showBroadcast) {
            cleanups.push(listen("siren:broadcastConfig:changed", ({clientID, departmentID, deleted}) => {
                if (clientID !== item.id) return;
                if (departmentID) {
                    setBroadcastDepts(n => deleted ? Math.max(0, n - 1) : n + 1);
                } else {
                    setBroadcastClient(!deleted);
                }
            }));
        }
        if (showDelivery) {
            cleanups.push(listen("siren:deliveryMethods:changed", ({clientID, departmentID, deleted}) => {
                if (clientID !== item.id) return;
                if (departmentID) {
                    setDeliveryDepts(n => deleted ? Math.max(0, n - 1) : n + 1);
                } else {
                    setDeliveryClient(!deleted);
                }
            }));
        }
        if (showEmail) {
            cleanups.push(listen("siren:emailBranding:changed", ({clientID, departmentID, deleted}) => {
                if (clientID !== item.id) return;
                if (departmentID) {
                    setEmailDepts(n => deleted ? Math.max(0, n - 1) : n + 1);
                } else {
                    setEmailClient(!deleted);
                }
            }));
        }
        if (showPhone) {
            cleanups.push(listen("siren:phoneCallConfig:changed", ({clientID, departmentID, deleted}) => {
                if (clientID !== item.id) return;
                if (departmentID) {
                    setPhoneDepts(n => deleted ? Math.max(0, n - 1) : n + 1);
                } else {
                    setPhoneClient(!deleted);
                }
            }));
        }
        if (showSMS) {
            cleanups.push(listen("siren:smsConfig:changed", ({clientID, departmentID, deleted}) => {
                if (clientID !== item.id) return;
                if (departmentID) {
                    setSMSDepts(n => deleted ? Math.max(0, n - 1) : n + 1);
                } else {
                    setSMSClient(!deleted);
                }
            }));
        }
        return () => cleanups.forEach(fn => fn());
    }, [item.id, showBroadcast, showDelivery, showEmail, showPhone, showSMS, listen]);

    if (!showBroadcast && !showDelivery && !showEmail && !showPhone && !showSMS) {
        return <span className="text-sm font-medium truncate">{item.name}</span>;
    }
    return (
        <div className="flex items-center justify-between gap-2 min-w-0">
            <span className="text-sm font-medium truncate">{item.name}</span>
            <div className="flex items-center gap-1 shrink-0">
                {showBroadcast && (
                    <div className="flex items-center gap-0.5">
                        <Dot active={broadcastClient}/>
                        <Dot active={broadcastDepts > 0}/>
                    </div>
                )}
                {showDelivery && (
                    <div className="flex items-center gap-0.5">
                        <Dot active={deliveryClient}/>
                        <Dot active={deliveryDepts > 0}/>
                    </div>
                )}
                {showEmail && (
                    <div className="flex items-center gap-0.5">
                        <Dot active={emailClient}/>
                        <Dot active={emailDepts > 0}/>
                    </div>
                )}
                {showPhone && (
                    <div className="flex items-center gap-0.5">
                        <Dot active={phoneClient}/>
                        <Dot active={phoneDepts > 0}/>
                    </div>
                )}
                {showSMS && (
                    <div className="flex items-center gap-0.5">
                        <Dot active={smsClient}/>
                        <Dot active={smsDepts > 0}/>
                    </div>
                )}
            </div>
        </div>
    );
}

function ClientLevelDots({clientID, hydrate, initialBroadcast, initialDelivery, initialEmail, initialPhone, initialSMS}: {clientID: string; hydrate?: string; initialBroadcast?: boolean; initialDelivery?: boolean; initialEmail?: boolean; initialPhone?: boolean; initialSMS?: boolean}) {
    const showBroadcast = hydrate?.includes("broadcastConfigExistence");
    const showDelivery = hydrate?.includes("deliveryMethodsExistence");
    const showEmail = hydrate?.includes("emailBrandingExistence");
    const showPhone = hydrate?.includes("phoneCallConfigExistence");
    const showSMS = hydrate?.includes("smsConfigExistence");
    const {listen} = useEvents();

    const [broadcastActive, setBroadcastActive] = useState(!!initialBroadcast);
    const [deliveryActive, setDeliveryActive] = useState(!!initialDelivery);
    const [emailActive, setEmailActive] = useState(!!initialEmail);
    const [phoneActive, setPhoneActive] = useState(!!initialPhone);
    const [smsActive, setSMSActive] = useState(!!initialSMS);

    useEffect(() => { setBroadcastActive(!!initialBroadcast); }, [initialBroadcast]);
    useEffect(() => { setDeliveryActive(!!initialDelivery); }, [initialDelivery]);
    useEffect(() => { setEmailActive(!!initialEmail); }, [initialEmail]);
    useEffect(() => { setPhoneActive(!!initialPhone); }, [initialPhone]);
    useEffect(() => { setSMSActive(!!initialSMS); }, [initialSMS]);

    useEffect(() => {
        const cleanups: (() => void)[] = [];
        if (showBroadcast) {
            cleanups.push(listen("siren:broadcastConfig:changed", ({clientID: cid, departmentID, deleted}) => {
                if (cid !== clientID || departmentID) return;
                setBroadcastActive(!deleted);
            }));
        }
        if (showDelivery) {
            cleanups.push(listen("siren:deliveryMethods:changed", ({clientID: cid, departmentID, deleted}) => {
                if (cid !== clientID || departmentID) return;
                setDeliveryActive(!deleted);
            }));
        }
        if (showEmail) {
            cleanups.push(listen("siren:emailBranding:changed", ({clientID: cid, departmentID, deleted}) => {
                if (cid !== clientID || departmentID) return;
                setEmailActive(!deleted);
            }));
        }
        if (showPhone) {
            cleanups.push(listen("siren:phoneCallConfig:changed", ({clientID: cid, departmentID, deleted}) => {
                if (cid !== clientID || departmentID) return;
                setPhoneActive(!deleted);
            }));
        }
        if (showSMS) {
            cleanups.push(listen("siren:smsConfig:changed", ({clientID: cid, departmentID, deleted}) => {
                if (cid !== clientID || departmentID) return;
                setSMSActive(!deleted);
            }));
        }
        return () => cleanups.forEach(fn => fn());
    }, [clientID, showBroadcast, showDelivery, showEmail, showPhone, showSMS, listen]);

    if (!showBroadcast && !showDelivery && !showEmail && !showPhone && !showSMS) return null;
    return (
        <div className="flex items-center gap-1 ml-auto">
            {showBroadcast && <Dot active={broadcastActive}/>}
            {showDelivery && <Dot active={deliveryActive}/>}
            {showEmail && <Dot active={emailActive}/>}
            {showPhone && <Dot active={phoneActive}/>}
            {showSMS && <Dot active={smsActive}/>}
        </div>
    );
}

function DeptItemRow({item, hydrate}: {item: DeptItem; selected: boolean; hydrate?: string}) {
    const showBroadcast = hydrate?.includes("broadcastConfigExistence");
    const showDelivery = hydrate?.includes("deliveryMethodsExistence");
    const showEmail = hydrate?.includes("emailBrandingExistence");
    const showPhone = hydrate?.includes("phoneCallConfigExistence");
    const showSMS = hydrate?.includes("smsConfigExistence");
    const {listen} = useEvents();

    const [broadcastActive, setBroadcastActive] = useState(!!item.hasSirenBroadcastConfig);
    const [deliveryActive, setDeliveryActive] = useState(!!item.hasSirenDeliveryMethods);
    const [emailActive, setEmailActive] = useState(!!item.hasSirenEmailBranding);
    const [phoneActive, setPhoneActive] = useState(!!item.hasSirenPhoneCallConfig);
    const [smsActive, setSMSActive] = useState(!!item.hasSirenSMSConfig);

    useEffect(() => {
        const cleanups: (() => void)[] = [];
        if (showBroadcast) {
            cleanups.push(listen("siren:broadcastConfig:changed", ({departmentID, deleted}) => {
                if (departmentID === item.id) setBroadcastActive(!deleted);
            }));
        }
        if (showDelivery) {
            cleanups.push(listen("siren:deliveryMethods:changed", ({departmentID, deleted}) => {
                if (departmentID === item.id) setDeliveryActive(!deleted);
            }));
        }
        if (showEmail) {
            cleanups.push(listen("siren:emailBranding:changed", ({departmentID, deleted}) => {
                if (departmentID === item.id) setEmailActive(!deleted);
            }));
        }
        if (showPhone) {
            cleanups.push(listen("siren:phoneCallConfig:changed", ({departmentID, deleted}) => {
                if (departmentID === item.id) setPhoneActive(!deleted);
            }));
        }
        if (showSMS) {
            cleanups.push(listen("siren:smsConfig:changed", ({departmentID, deleted}) => {
                if (departmentID === item.id) setSMSActive(!deleted);
            }));
        }
        return () => cleanups.forEach(fn => fn());
    }, [item.id, showBroadcast, showDelivery, showEmail, showPhone, showSMS, listen]);

    if (!showBroadcast && !showDelivery && !showEmail && !showPhone && !showSMS) {
        return <span className="text-sm font-medium truncate">{item.name}</span>;
    }
    return (
        <div className="flex items-center justify-between gap-2 min-w-0">
            <span className="text-sm font-medium truncate">{item.name}</span>
            <div className="flex items-center gap-1 shrink-0">
                {showBroadcast && <Dot active={broadcastActive}/>}
                {showDelivery && <Dot active={deliveryActive}/>}
                {showEmail && <Dot active={emailActive}/>}
                {showPhone && <Dot active={phoneActive}/>}
                {showSMS && <Dot active={smsActive}/>}
            </div>
        </div>
    );
}

type Props = {
    selectedScope: ScopeSelection | null;
    onSelect: (scope: ScopeSelection) => void;
    onClear?: () => void;
    onDrill?: (clientID: string, clientName: string) => void;
    initialClientID?: string | null;
    initialClientName?: string | null;
    deptPage?: number;
    onDeptPageChange?: (page: number) => void;
    hydrate?: string;
    showSystemDefault?: boolean;
    showClientLevel?: boolean;
};

export const ScopeSplitSelector: React.FC<Props> = ({selectedScope, onSelect, onClear, onDrill, initialClientID, initialClientName, deptPage, onDeptPageChange, hydrate, showSystemDefault = true, showClientLevel = true}) => {
    const initClient = selectedScope?.client ?? (initialClientID ? {id: initialClientID, name: initialClientName ?? ""} : null);
    const [selectedClient, setSelectedClient] = useState<ClientItem | null>(initClient);

    // Sync selectedClient when scope/drill resolves asynchronously (e.g. from URL params on load)
    useEffect(() => {
        const client = selectedScope?.client ?? (initialClientID ? {id: initialClientID, name: initialClientName ?? ""} : null);
        if (client) {
            setSelectedClient(prev => (!prev || prev.id !== client.id) ? client : prev);
        }
    }, [selectedScope?.client?.id, initialClientID]);

    // When the client is pre-set (auto-drilled, no list step), fetch hydration data so dots render correctly.
    useEffect(() => {
        if (!hydrate || !selectedClient) return;
        const hasHydration = selectedClient.sirenBroadcastConfig !== undefined
            || selectedClient.sirenDeliveryMethods !== undefined
            || selectedClient.sirenEmailBranding !== undefined
            || selectedClient.sirenPhoneCallConfig !== undefined
            || selectedClient.sirenSMSConfig !== undefined;
        if (hasHydration) return;
        platformAPI.get<{items: ClientItem[]}>("api/clients", {params: {search: "", page: 1, limit: 1, module: "siren", hydrate}})
            .then(res => {
                const match = res.data.items.find(c => c.id === selectedClient.id);
                if (match) setSelectedClient(match);
            })
            .catch(() => {});
    }, [selectedClient?.id, hydrate]);

    const getClients = useCallback(async (search: string, page: number): Promise<SelectListPage<ClientItem>> => {
        const params: Record<string, string | number> = {search, page: page + 1, limit: PAGE_SIZE, module: "siren"};
        if (hydrate) params.hydrate = hydrate;
        const res = await platformAPI.get<{items: ClientItem[]; totalPages: number}>("api/clients", {params});
        const rows: SelectListItem<ClientItem>[] = res.data.items.map(c => ({item: c, label: c.name}));
        return {items: rows, totalPages: Math.max(1, res.data.totalPages)};
    }, [hydrate]);

    const getDepartments = useCallback(async (search: string, page: number): Promise<SelectListPage<DeptItem>> => {
        if (!selectedClient) return {items: [], totalPages: 1};
        try {
const params: Record<string, string | number> = {clientID: selectedClient.id, search, page: page + 1, limit: PAGE_SIZE};
            if (hydrate) params.hydrate = hydrate;
            const res = await platformAPI.get<{items: DeptItem[]; totalPages: number}>("api/departments", {params});
            const rows: SelectListItem<DeptItem>[] = res.data.items.map(d => ({
                item: d,
                label: d.name,
            }));
            return {items: rows, totalPages: Math.max(1, res.data.totalPages)};
        } catch {
            return {items: [], totalPages: 1};
        }
    }, [selectedClient, hydrate]);

    const isSystemDefaultSelected = selectedScope !== null && selectedScope.client === null;
    const selectedClientID = selectedScope?.client?.id ?? null;

    const isClientLevelSelected = selectedScope?.client?.id === selectedClient?.id && selectedScope?.department === null;
    const selectedDeptID = selectedScope?.client?.id === selectedClient?.id && selectedScope?.department
        ? selectedScope.department.id
        : null;

    const systemDefaultHeader = (
        <div className="border-b border-light-light-grey">
            <button
                type="button"
                className={`flex items-center gap-1.5 w-full px-4 py-3 text-left text-sm transition-colors ${
                    isSystemDefaultSelected ? "bg-blue text-white" : "hover:bg-light-light-grey"
                }`}
                onClick={() => onSelect({client: null, department: null})}
            >
                <PublicIcon style={{fontSize: 16}}/>
                System Default
            </button>
        </div>
    );

    if (!selectedClient) {
        return (
            <SelectList<ClientItem>
                getItems={getClients}
                selectedID={selectedClientID}
                pageSize={PAGE_SIZE}
                onSelect={c => {
                    onClear?.();
                    setSelectedClient(c);
                    onDrill?.(c.id, c.name);
                }}
                renderItem={(c, selected) => (
                    <ClientItemRow item={c} selected={selected} hydrate={hydrate}/>
                )}
                header={showSystemDefault ? systemDefaultHeader : undefined}
            />
        );
    }

    const backHeader = (
        <div className="flex flex-col border-b border-light-light-grey">
            {showSystemDefault && (
                <button
                    className="flex items-center gap-2 w-full px-4 py-3 text-sm font-medium text-blue hover:bg-light-light-grey transition-colors border-b border-light-light-grey"
                    onClick={() => { setSelectedClient(null); onClear?.(); }}
                >
                    <ArrowBackIcon style={{fontSize: 16}}/>
                    <span>Clients</span>
                    <span className="text-light-grey font-normal">/ {selectedClient.name}</span>
                </button>
            )}
            {showClientLevel && (
                <button
                    type="button"
                    className={`flex items-center gap-1.5 w-full px-4 py-3 text-left text-sm transition-colors ${
                        isClientLevelSelected ? "bg-blue text-white" : "hover:bg-light-light-grey"
                    }`}
                    onClick={() => onSelect({client: selectedClient, department: null})}
                >
                    <DomainIcon style={{fontSize: 16}}/>
                    Client level
                    <ClientLevelDots clientID={selectedClient.id} hydrate={hydrate} initialBroadcast={!!selectedClient.sirenBroadcastConfig?.client} initialDelivery={!!selectedClient.sirenDeliveryMethods?.client} initialEmail={!!selectedClient.sirenEmailBranding?.client} initialPhone={!!selectedClient.sirenPhoneCallConfig?.client} initialSMS={!!selectedClient.sirenSMSConfig?.client}/>
                </button>
            )}
        </div>
    );

    return (
        <SelectList<DeptItem>
            key={selectedClient.id}
            getItems={getDepartments}
            selectedID={selectedDeptID}
            pageSize={PAGE_SIZE}
            initialPage={deptPage}
            onPageChange={onDeptPageChange}
            onSelect={row => {
                onSelect({client: selectedClient, department: {id: row.id, name: row.name} as unknown as Department});
            }}
            renderItem={(d, selected) => (
                <DeptItemRow item={d} selected={selected} hydrate={hydrate}/>
            )}
            header={backHeader}
        />
    );
};
